import { Box } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { CustomHeading } from 'shared-components';
import { ApplicationState } from '../../../../../store';
import Field from '../../../../Field/Field';
import { PrevNextButtons } from '../../../../PrevNextButtons';

import { useNavigate } from 'react-router-dom';
import { FeeStatus, FundingOption } from 'api-paths-cas';
import ApplicantHeader from '../../../../../components/molecules/applicantHeader';
import { SectionHeader, SectionTitles } from '@/components/molecules/SectionHeader';
import { getApplicationData } from '@/components/pages/applicant-dashboard/sections/helpers/getApplicationData';
import { useTaskRouteGuard } from '@/hooks/useTaskRouteGuard';
import { TaskLoadingSkeleton } from '@/components/molecules/TaskLoadingSkeleton';
import { useTaskNavigation } from '@/hooks/useTaskNavigation';

export default function Funding() {
  const { application } = useSelector((state: ApplicationState) => state.apply);
  const { applyConfig } = useSelector((state: ApplicationState) => state.applyConfig);

  const navigate = useNavigate();
  const { nextRoute, previousRoute } = useTaskNavigation();
  // Route guard - redirects if task is disabled
  const { isLoading } = useTaskRouteGuard('/funding');

  if (isLoading) {
    return <TaskLoadingSkeleton />;
  }

  const { initial, current } = getApplicationData(application);
  const { primaryFundingSource, isSLCGrant, isSLCLoan, isPlanningToWork, feeStatus } = current;
  const {
    primaryFundingSource: initialPrimaryFundingSource,
    isSLCGrant: initialIsSLCGrant,
    isSLCLoan: initialIsSLCLoan,
    isPlanningToWork: initialIsPlanningToWork,
    feeStatus: initialFeeStatus,
  } = initial;

  const showFeeStatusFields = feeStatus === FeeStatus.Home || initialFeeStatus === FeeStatus.Home;

  return (
    <>
      <ApplicantHeader pageName="Application" />
      <Box>
        <SectionHeader title={SectionTitles.FUNDING} />

        <CustomHeading level="h2" mb={2} mt={6} fontSize="md">
          Data
        </CustomHeading>

        <Box>
          <Field
            leftLabel="Primary funding source"
            value={FundingOptionText[primaryFundingSource]}
            originalValue={FundingOptionText[initialPrimaryFundingSource]}
          />
          {showFeeStatusFields && (
            <>
              {!applyConfig?.disableFundingTuitionFeeQuestion && (
                <Field leftLabel="SLC tuition fee funding" value={isSLCGrant} originalValue={initialIsSLCGrant} />
              )}
              {!applyConfig?.disableFundingMaintenanceQuestion && (
                <Field leftLabel="SLC maintenance loan" value={isSLCLoan} originalValue={initialIsSLCLoan} />
              )}
            </>
          )}
          {!applyConfig?.disableFundingWorkQuestion && (
            <Field leftLabel="Work through study" value={isPlanningToWork} originalValue={initialIsPlanningToWork} />
          )}
        </Box>

        <Box mt={6}>
          <PrevNextButtons
            onPrevious={previousRoute ? () => navigate(previousRoute) : undefined}
            onNext={nextRoute ? () => navigate(nextRoute) : undefined}
          />
        </Box>
      </Box>
    </>
  );
}

export const FundingOptionText: Record<FundingOption, string> = {
  [FundingOption.PrivateFinance]: 'Private finance',
  [FundingOption.StudentFinance]: 'Student finance services (UK, Chl, IoM or EU)',
  [FundingOption.HealthAuthority]: 'Department of Health or Regional Health Authority',
  [FundingOption.ResearchCouncils]: 'Research councils',
  [FundingOption.TrainingAgency]: 'Training agency',
  [FundingOption.UkIndustry]: 'UK industry or commerce',
  [FundingOption.UkGovtInternational]: 'UK Government international award',
  [FundingOption.UkGovtOther]: 'Other UK Government award',
  [FundingOption.UniversityScholarship]: 'University Scholarship',
  [FundingOption.OverseasAgency]: 'Overseas agency',
  [FundingOption.OverseasGovt]: 'Overseas government award',
  [FundingOption.OverseasIndustry]: 'Overseas industry or commerce',
  [FundingOption.OverseasUniversity]: 'Overseas university',
  [FundingOption.Other]: 'Other',
  [FundingOption.Unknown]: "I don't know",
};
